import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate, formatDateTime } from '../lib/utils';
import { PageHeader } from '../components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { StatusBadge } from '../components/shared/StatusBadge';
import { UrgencyBadge } from '../components/shared/UrgencyBadge';
import { PdfPreviewModal } from '../components/shared/PdfPreviewModal';
import { useAppContext } from '../lib/store';
import { Edit, FileDown, Copy, Mail, ArrowLeft, Clock, User } from 'lucide-react';
import { toast } from 'sonner';
export function WorkOrderDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    workOrders,
    customers,
    users,
    duplicateWorkOrder
  } = useAppContext();
  const [showPdf, setShowPdf] = useState(false);
  const workOrder = workOrders.find(wo => wo.id === id);
  if (!workOrder) {
    return <div data-id="element-441">Delovni nalog ne obstaja.</div>;
  }
  const customer = customers.find(c => c.id === workOrder.customer_id);
  const assignedUser = users.find(u => u.id === workOrder.assigned_user_id);
  const createdBy = users.find(u => u.id === workOrder.created_by);
  const updatedBy = users.find(u => u.id === workOrder.updated_by);
  const handleDuplicate = () => {
    duplicateWorkOrder(workOrder.id);
    navigate('/nalogi');
  };
  const handleEmail = () => {
    toast.success('Pripravljam e-poštno sporočilo s pripetim PDF dokumentom...');
  };
  return <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-442">
      <div className="flex items-center gap-2 mb-2" data-id="element-443">
        <Button variant="ghost" size="sm" onClick={() => navigate('/nalogi')} className="text-slate-500" data-id="element-444">
          <ArrowLeft className="w-4 h-4 mr-1" data-id="element-445" /> Nazaj na seznam
        </Button>
      </div>

      <PageHeader title={`Delovni nalog ${workOrder.work_order_number}`} description={`Ustvarjeno ${formatDate(workOrder.created_at)}`} data-id="element-446">
        <div className="flex flex-wrap gap-2" data-id="element-447">
          <Button variant="outline" onClick={() => navigate(`/nalogi/${id}/uredi`)} data-id="element-448">
            <Edit className="w-4 h-4 mr-2" data-id="element-449" /> Uredi
          </Button>
          <Button variant="outline" onClick={() => setShowPdf(true)} data-id="element-450">
            <FileDown className="w-4 h-4 mr-2" data-id="element-451" /> PDF
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-452">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6" data-id="element-453">
          <Card data-id="element-454">
            <CardHeader className="pb-3" data-id="element-455">
              <CardTitle className="text-lg" data-id="element-456">Podatki o nalogu</CardTitle>
            </CardHeader>
            <CardContent data-id="element-457">
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-458">
                <div data-id="element-459">
                  <p className="text-slate-500 mb-1" data-id="element-460">Stranka</p>
                  <p className="font-medium" data-id="element-461">{customer?.name || '/'}</p>
                </div>
                <div data-id="element-462">
                  <p className="text-slate-500 mb-1" data-id="element-463">Datum izvedbe</p>
                  <p className="font-medium" data-id="element-464">
                    {new Date(workOrder.execution_date).toLocaleDateString('sl-SI')}
                  </p>
                </div>
                <div className="col-span-2" data-id="element-465">
                  <p className="text-slate-500 mb-1" data-id="element-466">Lokacija</p>
                  <p className="font-medium" data-id="element-467">{workOrder.location}</p>
                </div>
                <div data-id="element-468">
                  <p className="text-slate-500 mb-1" data-id="element-469">Št. / Naročnik</p>
                  <p className="font-medium" data-id="element-470">
                    {workOrder.customer_reference_or_orderer || '/'}
                  </p>
                </div>
                <div data-id="element-471">
                  <p className="text-slate-500 mb-1" data-id="element-472">Izvajalec</p>
                  <p className="font-medium" data-id="element-473">
                    {assignedUser?.full_name || '/'}
                  </p>
                </div>
              </div>

              {workOrder.work_description && <div className="mt-6 pt-6 border-t" data-id="element-474">
                  <p className="text-slate-500 mb-2 text-sm" data-id="element-475">Opis opravila</p>
                  <p className="whitespace-pre-wrap text-sm" data-id="element-476">
                    {workOrder.work_description}
                  </p>
                </div>}
            </CardContent>
          </Card>

          {/* Labor */}
          <Card data-id="element-477">
            <CardHeader className="pb-3" data-id="element-478">
              <CardTitle className="text-lg" data-id="element-479">Opravljeno delo</CardTitle>
            </CardHeader>
            <CardContent data-id="element-480">
              {workOrder.labor_items.length > 0 ? <Table data-id="element-481">
                  <TableHeader data-id="element-482">
                    <TableRow data-id="element-483">
                      <TableHead data-id="element-484">Opis</TableHead>
                      <TableHead className="text-right w-24" data-id="element-485">
                        Količina
                      </TableHead>
                      <TableHead className="w-24" data-id="element-486">EM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-487">
                    {workOrder.labor_items.map(item => <TableRow key={item.id} data-id="element-488">
                        <TableCell data-id="element-489">{item.description}</TableCell>
                        <TableCell className="text-right" data-id="element-490">
                          {item.quantity}
                        </TableCell>
                        <TableCell data-id="element-491">{item.unit}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table> : <p className="text-sm text-slate-500 italic" data-id="element-492">
                  Ni vnesenega dela.
                </p>}
            </CardContent>
          </Card>

          {/* Material */}
          <Card data-id="element-493">
            <CardHeader className="pb-3" data-id="element-494">
              <CardTitle className="text-lg" data-id="element-495">Porabljen material</CardTitle>
            </CardHeader>
            <CardContent data-id="element-496">
              {workOrder.material_items.length > 0 ? <Table data-id="element-497">
                  <TableHeader data-id="element-498">
                    <TableRow data-id="element-499">
                      <TableHead data-id="element-500">Naziv materiala</TableHead>
                      <TableHead className="text-right w-24" data-id="element-501">
                        Količina
                      </TableHead>
                      <TableHead className="w-24" data-id="element-502">EM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-503">
                    {workOrder.material_items.map(item => <TableRow key={item.id} data-id="element-504">
                        <TableCell data-id="element-505">{item.material_name}</TableCell>
                        <TableCell className="text-right" data-id="element-506">
                          {item.quantity}
                        </TableCell>
                        <TableCell data-id="element-507">{item.unit}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table> : <p className="text-sm text-slate-500 italic" data-id="element-508">
                  Ni vnesenega materiala.
                </p>}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-509">
          <Card data-id="element-510">
            <CardHeader className="pb-3" data-id="element-511">
              <CardTitle className="text-lg" data-id="element-512">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-513">
              <div data-id="element-514">
                <p className="text-sm text-slate-500 mb-2" data-id="element-515">Trenutni status</p>
                <StatusBadge status={workOrder.status} data-id="element-516" />
              </div>
              <Separator data-id="element-517" />
              <div data-id="element-518">
                <p className="text-sm text-slate-500 mb-2" data-id="element-519">Nujnost</p>
                <UrgencyBadge urgency={workOrder.urgency} data-id="element-520" />
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-521">
            <CardHeader className="pb-3" data-id="element-522">
              <CardTitle className="text-lg" data-id="element-523">Podpisi in opombe</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-524">
              <div data-id="element-525">
                <p className="text-sm text-slate-500 mb-1" data-id="element-526">Izvajalec</p>
                <p className="font-medium text-sm" data-id="element-527">
                  {workOrder.technician_name || 'Ni podpisa'}
                </p>
              </div>
              <div data-id="element-528">
                <p className="text-sm text-slate-500 mb-1" data-id="element-529">Naročnik</p>
                <p className="font-medium text-sm" data-id="element-530">
                  {workOrder.orderer_name || 'Ni podpisa'}
                </p>
              </div>

              {workOrder.notes && <>
                  <Separator data-id="element-531" />
                  <div data-id="element-532">
                    <p className="text-sm text-slate-500 mb-1" data-id="element-533">
                      Interne opombe
                    </p>
                    <p className="text-sm whitespace-pre-wrap" data-id="element-534">
                      {workOrder.notes}
                    </p>
                  </div>
                </>}
            </CardContent>
          </Card>

          <Card data-id="element-535">
            <CardHeader className="pb-3" data-id="element-536">
              <CardTitle className="text-lg" data-id="element-537">Zgodovina</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm" data-id="element-538">
              <div className="flex gap-3" data-id="element-539">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-540" />
                <div data-id="element-541">
                  <p className="font-medium" data-id="element-542">Ustvarjeno</p>
                  <p className="text-slate-500" data-id="element-543">
                    {formatDateTime(workOrder.created_at)}
                  </p>
                  <p className="text-slate-500" data-id="element-544">{createdBy?.full_name}</p>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-545">
                <User className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-546" />
                <div data-id="element-547">
                  <p className="font-medium" data-id="element-548">Zadnja sprememba</p>
                  <p className="text-slate-500" data-id="element-549">
                    {new Date(workOrder.updated_at).toLocaleString('sl-SI')}
                  </p>
                  <p className="text-slate-500" data-id="element-550">{updatedBy?.full_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PdfPreviewModal open={showPdf} onOpenChange={setShowPdf} workOrder={workOrder} data-id="element-551" />
    </div>;
}